import React, { useState, useEffect, useRef, useContext } from 'react';
import './RemoteParkingActive.scss';
import VideoScroll from '@/v2/components/media/video/VideoScroll';
import Header from '@/v2/components/application/Header';
import TimelineText from '@/v2/components/atoms/TimelineText';
import ScrollController from '@/v2/components/controllers/ScrollController';
import TimeController from '@/v2/components/controllers/TimeController';
import TextBottom from '@/v2/components/atoms/TextBottom';
import Button from '@/v2/components/atoms/Button';
import Fade from '@/v2/components/atoms/Fade';
import SwipeController from '@/v2/components/controllers/SwipeController';
import ScrollMouse from '@/v2/components/atoms/Icon/ScrollMouse';
import ScrollSwipe from '@/v2/components/atoms/Icon/ScrollSwipe';
import { UseSimulatedScrolling } from '@/v2/components/utils/DeviceUtils';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { DriverAssistContext } from '@/v2/state/providers';
import { MomentStateContext } from '@/v2/state/momentState';
import { timeout, saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';

const STATE = {
    none: 'none',
    intro: 'intro',
    cta: 'cta',
    video: 'video',
    timeline: 'timeline',
    end: 'end',
    proceed: 'proceed',
};

let scrolledToFinishVideo = false;

const videoReady = (controller, videoController, swipeController, scrollController, refContainer, onReady, remoteParkingActiveTracking) => {
    // eslint-disable-next-line no-param-reassign
    videoController.current = controller;

    controller.Play(); // Fix later. Not best preloading technique.

    let updatedOnce = false;

    // eslint-disable-next-line no-param-reassign
    scrollController.current = new ScrollController(controller.GetEndTime(), 30, {
        fixedUpdate: true,
        manualScrollDirection: UseSimulatedScrolling(),
        onTime: (time) => {
            if (!updatedOnce && time > 0.1) {
                updatedOnce = true;
                remoteParkingActiveTracking();
            }

            controller.SeekTime(time);
        },
    });

    if (UseSimulatedScrolling()) {
        // eslint-disable-next-line no-param-reassign
        swipeController.current = new SwipeController(refContainer.current);

        swipeController.current.onUp(() => {
            scrollController.current.SetScrollDirection(ScrollController.SCROLL_DIRECTION.forward);
        });

        swipeController.current.onDown(() => {
            scrollController.current.SetScrollDirection(ScrollController.SCROLL_DIRECTION.backwards);
        });

        swipeController.current.onLeft(() => {});

        swipeController.current.onRight(() => {});
    }

    onReady();
};

export default ({ isActive, data, onFinished, onReady }) => {
    const [state, setState] = useState(STATE.none);
    const [scrollMouseAnimate, setScrollMouseAnimate] = useState(true);
    const [textAnimate, setTextAnimate] = useState('out');
    const [scrollTextAnimate, setScrollTextAnimate] = useState('out');
    const [btnAnimate, setBtnAnimate] = useState('in');

    const videoController = useRef(null);
    const scrollController = useRef(null);
    const timeController = useRef(null);
    const swipeController = useRef(null);

    const refContainer = useRef();

    const time = useRef(TimelineText.Time());
    const { trackEvent } = useContext(AnalyticsContext);

    const [{ disclaimer }] = useContext(DriverAssistContext);

    const { dispatch: momentDispatch } = useContext(MomentStateContext);

    const videoFinished = () => {
        timeout(() => {
            setState(STATE.proceed);
        }, 200);
    };

    const handleCtaClick = () => {
        setBtnAnimate('out');
        timeout(() => {
            setState(STATE.end);
        }, 800);

        timeout(() => {
            onFinished();
        }, 1800);
    };

    const stateCta = () => {
        scrollController.current.Start();

        timeout(() => {
            setScrollMouseAnimate(false);

            if (UseSimulatedScrolling()) {
                swipeController.current.run();
            }
        }, 800);
    };

    const stateTimeline = (cb) => {
        saveTimeout(
            timeController.current.AddTimeoutFromTimeline(data.text.secondaryTimeline, () => {
                setTimeout(() => {
                    cb();
                }, 1000);
            })
        );
    };

    const stateIntro = (cb) => {
        if (videoController && videoController.current) {
            videoController.current.Pause(); // Fix later. Not best preloading technique.
            videoController.current.SeekTime(0); // Fix later. Not best preloading technique.
        }

        // eslint-disable-next-line no-param-reassign
        timeController.current = new TimeController(time.current.Set);
        saveTimeout(
            timeController.current.AddTimeoutFromTimeline(data.text.timeline, () => {
                setTextAnimate('out');
                setTimeout(() => {
                    cb();
                    setScrollTextAnimate('in');
                }, 1000);
            })
        );
    };

    useEffect(() => {
        if (isActive) setState(STATE.intro);
    }, [isActive]);

    useEffect(() => {
        if (isActive && videoController && videoController.current) {
            videoController.current.ProgressDispatch(momentDispatch);
        }
    }, [isActive, videoController.current]);

    useEffect(() => {
        switch (state) {
            case STATE.intro:
                stateIntro(() => setState(STATE.cta));
                break;
            case STATE.cta:
                stateCta(swipeController, scrollController, setScrollMouseAnimate);
                break;
            case STATE.timeline:
                stateTimeline(() => videoFinished());
                break;
            default:
        }
    }, [state]);

    useEffect(
        () => () => {
            if (scrollController.current) {
                scrollController.current.Stop();
            }
            clearTimeouts();
        },
        []
    );

    const options = {
        bufferBeforePlay: true,
        // eslint-disable-next-line max-len
        onReady: (controller) =>
            videoReady(controller, videoController, swipeController, scrollController, refContainer, onReady, () =>
                trackEvent('vcClick', {
                    vcStep: 'driverAssist',
                    vcClick: 'scroll:move the car',
                })
            ),
        onFinished: () => {
            if (!scrolledToFinishVideo) {
                scrolledToFinishVideo = true;
                setScrollTextAnimate('out');

                timeout(() => {
                    setState(STATE.timeline);
                }, 800);
            }
        },
        minimumBuffer: 1,
        autoPlay: false,
        loop: false,
    };

    const timeline = state === STATE.timeline ? data.text.secondaryTimeline : data.text.timeline;
    const disclaimerContent = disclaimer ? <Disclaimer data={disclaimer} dark /> : '';

    return (
        <div className="RemoteParkingActive" ref={refContainer}>
            <VideoScroll src={data.video} options={options} />
            <Header show hideLogo dark />
            <TimelineText data={timeline} time={time.current} key={`timeline-${state}`} background="dark" color="black" />
            <TextBottom background="dark" animate={textAnimate} color="black" component="h4">
                {data.text.text}
            </TextBottom>
            <div className="RemoteParkingActive__scrollText">
                <TextBottom background="dark" animate={scrollTextAnimate} color="black" component="h4">
                    {data.text.cta.scroll}
                    <i className="Icon__scrollMouse">
                        <ScrollMouse loop={false} isstopped={scrollMouseAnimate} ispaused={scrollMouseAnimate} />
                    </i>
                    <i className="Icon__scrollSwipe">
                        <ScrollSwipe loop={false} isstopped={scrollMouseAnimate} ispaused={scrollMouseAnimate} />
                    </i>
                </TextBottom>
            </div>
            {state === STATE.proceed && (
                <div className="RemoteParkingActive__cta">
                    <Button
                        variant="base"
                        animate={btnAnimate}
                        onClick={() => {
                            handleCtaClick();
                            trackEvent('vcClick', { vcStep: 'driverAssist', vcClick: 'hotspot:proceed' });
                        }}
                    >
                        {data.text.cta.proceed}
                    </Button>
                </div>
            )}
            <Fade show={!isActive || state === STATE.end} light cover />
            {disclaimerContent}
        </div>
    );
};
